import type { PlaceholderValue } from "@homeinstead/schemas";
import type { PortableTextBlock } from "@portabletext/types";

import { isValidPortableText } from "../../utils/filter-empty-blocks";

/**
 * Applied by a block that needs the full container width. Spans both tracks of
 * the grid, using explicit start and end lines so it does not depend on
 * stylesheet ordering against the `[&>*]:col-start-1` rule.
 */
const RICH_TEXT_CONTAINER_WIDTH_CLASS = "col-start-1 col-end-3";

/**
 * Track list used by the RichText wrapper and any placeholder hosts between it
 * and a container-width block. Each host must span and recreate both tracks so
 * a nested block can continue spanning past the prose measure.
 *
 * The content track comes first and the slack track absorbs the remainder on
 * the right, which preserves the left alignment `max-w-prose` gives today. A
 * symmetric `1fr … 1fr` track list would silently center every article body.
 *
 * Direct-child `[data-rich-text-gap]` markers (`<br>`) need an explicit block
 * size here: a bare `<br>` collapses as a grid item. Prose-mode RichText leaves
 * those markers unstyled so spacing matches historical `<br>` empty blocks.
 */
const RICH_TEXT_CONTAINER_WIDTH_GRID_CLASS =
  "grid grid-cols-[min(65ch,100%)_minmax(0,1fr)] [&>*]:col-start-1 [&>[data-rich-text-gap]]:block [&>[data-rich-text-gap]]:h-[1em]";

type DynamicPlaceholderMarkDef = {
  _key: string;
  _type: "dynamicPlaceholder";
  placeholderKey?: string;
};

type HasContainerWidthRichTextBlockOptions = {
  activePlaceholderKeys?: readonly string[];
  placeholders?: Record<string, PlaceholderValue>;
};

function blockNeedsContainerWidth(block: PortableTextBlock): boolean {
  return (
    block._type === "richTextColumns" &&
    (block as { width?: string }).width !== "prose"
  );
}

function getDynamicPlaceholderMarkDefs(
  block: PortableTextBlock,
): DynamicPlaceholderMarkDef[] {
  if (!Array.isArray(block.markDefs)) {
    return [];
  }

  return block.markDefs.flatMap((markDef) => {
    const isDynamicPlaceholderMarkDef =
      typeof markDef === "object" &&
      markDef !== null &&
      markDef._type === "dynamicPlaceholder";

    if (!isDynamicPlaceholderMarkDef) {
      return [];
    }

    return [markDef as DynamicPlaceholderMarkDef];
  });
}

/**
 * The Portable Text a placeholder resolves to, or `null` when it resolves to a
 * scalar or nothing.
 *
 * Only the resolved value can introduce a container-width block. The mark's
 * `fallbackText` is a `fallbackRichText` field, which the schema restricts to
 * inline text blocks (no columns, no nested placeholders), so it is never
 * consulted here.
 */
function resolvePlaceholderBlocks(
  placeholderKey: string,
  placeholders: Record<string, PlaceholderValue>,
): PortableTextBlock[] | null {
  const placeholderValue = placeholders[placeholderKey];

  if (
    Array.isArray(placeholderValue) &&
    isValidPortableText(placeholderValue)
  ) {
    return placeholderValue;
  }

  return null;
}

function hasContainerWidthInPlaceholderContent(
  markDef: DynamicPlaceholderMarkDef,
  options: HasContainerWidthRichTextBlockOptions,
): boolean {
  const placeholders = options.placeholders ?? {};
  const activePlaceholderKeys = options.activePlaceholderKeys ?? [];
  const placeholderKey = markDef.placeholderKey;
  if (typeof placeholderKey !== "string") {
    return false;
  }

  const isRecursivePlaceholder = activePlaceholderKeys.includes(placeholderKey);
  if (isRecursivePlaceholder) {
    return false;
  }

  const resolvedBlocks = resolvePlaceholderBlocks(placeholderKey, placeholders);

  if (!resolvedBlocks) {
    return false;
  }

  return hasContainerWidthRichTextBlock(resolvedBlocks, {
    activePlaceholderKeys: [...activePlaceholderKeys, placeholderKey],
    placeholders,
  });
}

/**
 * Whether any block in the value needs the full container width, which is what
 * puts the wrapper into `RICH_TEXT_CONTAINER_WIDTH_GRID_CLASS`.
 *
 * Checked per value rather than applied unconditionally so the existing
 * `RichText` call sites emit byte-identical markup: in grid mode the wrapper
 * itself becomes full width, which would widen any background or border a
 * caller passes through `className`.
 *
 * Dynamic placeholders are included so injected text content can opt into
 * the same grid when it contains full-width columns blocks.
 */
function hasContainerWidthRichTextBlock(
  blocks: PortableTextBlock[],
  options: HasContainerWidthRichTextBlockOptions = {},
): boolean {
  for (const block of blocks) {
    if (blockNeedsContainerWidth(block)) {
      return true;
    }

    for (const markDef of getDynamicPlaceholderMarkDefs(block)) {
      if (hasContainerWidthInPlaceholderContent(markDef, options)) {
        return true;
      }
    }
  }

  return false;
}

export {
  hasContainerWidthRichTextBlock,
  RICH_TEXT_CONTAINER_WIDTH_CLASS,
  RICH_TEXT_CONTAINER_WIDTH_GRID_CLASS,
};
